import React, { useContext, useEffect, useState, useCallback } from "react";
import { Badge, Flex, MTabs, MTable, MTag, Search, Text } from "@cisco/spaces-dash-lib-styleguide";
import dayjs from "dayjs";
import { I18nContext } from "../../contexts/i18nContext";
import { fetchRules, fetchRulesCount } from "../../service/business-hierarchy-service";
import useNotification from "../../hooks/useNotification";

function ProximityRules(props) {
  const { t } = useContext(I18nContext);
  const { setToastMessage } = useNotification();
  const {
    selectedNodeValue: { data: selectedNode }
  } = props;

  const [selectedTab, setSelectedTab] = useState("Captive Portal");
  const [cpRuleCount, setCpRuleCount] = useState(0);
  const [enRuleCount, setEnRuleCount] = useState(0);
  const [lpRuleCount, setLpRuleCount] = useState(0);
  const [densityRuleCount, setDensityRuleCount] = useState(0);
  const [searchString, setSearchString] = useState("");
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesList, setRulesList] = useState([]);

  const getRulesCount = useCallback(
    async (locationId) => {
      try {
        const res = await fetchRulesCount(locationId);
        if (res && res.status) {
          setCpRuleCount(res.data?.captivePortalRulesCount || 0);
          setEnRuleCount(res.data?.engagementRulesCount || 0);
          setLpRuleCount(res.data?.profileRulesCount || 0);
          setDensityRuleCount(res.data?.densityRulesCount || 0);
        }
      } catch (error) {
        console.error("error", error);
        setToastMessage("Error fetching rules count", 5000, "negative");
      }
    },
    [setToastMessage]
  );

  const getRules = useCallback(
    async (locationId) => {
      setRulesLoading(true);
      try {
        const res = await fetchRules(locationId, selectedTab);
        if (res && res.status) {
          setRulesLoading(false);
          const orderedRules = res.data.sort((a, b) => {
            if (a.state === "LIVE" && b.state !== "LIVE") return -1;
            if (a.state !== "LIVE" && b.state === "LIVE") return 1;
            return 0;
          });
          setRulesList(orderedRules);
        } else {
          setRulesLoading(false);
          setRulesList(res.data);
        }
      } catch (error) {
        setRulesLoading(false);
        console.error("error", error);
        setToastMessage("Error fetching rules", 5000, "negative");
      }
    },
    [selectedTab, setToastMessage]
  );

  const returnScheduleText = (rule) => {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    let str = "";
    if (rule.weekDaysSchedule && rule.weekDaysSchedule.length < 7) {
      str += `Every ${rule.weekDaysSchedule.map((day) => days[day - 1]).join(", ")}`;
    } else {
      str += "Everyday";
    }
    if (rule.scheduleTimeFrom && rule.scheduleTimeTo) {
      str += ` from ${dayjs(rule.scheduleTimeFrom).format("DD MMM YYYY")} to ${dayjs(rule.scheduleTimeTo).format("DD MMM YYYY")}`;
    }
    return str;
  };

  const getNameCellValue = ({ row }) => {
    return (
      <Flex direction="vertical" gap={4}>
        <Flex gap={8}>
          <Text size="p2" weight="bold">
            {row.original?.displayName}
          </Text>
          <MTag.Colored size="sm" color={row.original?.state === "LIVE" ? 8 : 10}>
            {row.original?.state}
          </MTag.Colored>
        </Flex>
      </Flex>
    );
  };

  const getScheduleCellValue = ({ row }) => {
    return (
      <Flex gap={4} align="center">
        {!row.original?.scheduleTimeFrom && !row.original?.weekDaysSchedule && (
          <Text size="p4" weight="semi-bold">
            {t("proximity-rule.everyday-text")}
          </Text>
        )}
        {(row.original?.scheduleTimeFrom || row.original?.weekDaysSchedule) && (
          <Flex>
            <Text size="p4" weight="semi-bold">
              {returnScheduleText(row.original)}
            </Text>
          </Flex>
        )}
      </Flex>
    );
  };

  const getColumns = () => {
    return [
      {
        accessorKey: "displayName",
        header: t("location-sensors.name-text"),
        cell: getNameCellValue
      },
      {
        accessorKey: "locations",
        header: "Locations"
      },
      {
        accessorKey: "",
        header: "Schedule",
        cell: getScheduleCellValue
      },
      {
        accessorKey: "createdOn",
        header: t("proximity-rule.created-on-text")
      },
      {
        accessorKey: "updatedOn",
        header: t("proximity-rule.last-modified-text")
      }
    ];
  };

  const getData = useCallback(() => {
    const data = rulesList.map((rule) => {
      return {
        ...rule,
        createdOn: dayjs(rule.createdOn).format("MMM DD YYYY, hh:mm A"),
        updatedOn: dayjs(rule.updatedOn).format("MMM DD YYYY, hh:mm A")
      };
    });

    return data;
  }, [rulesList]);

  const getFilteredData = useCallback(() => {
    return getData().filter((rule) => {
      return rule.displayName.toLowerCase().includes(searchString.toLowerCase());
    });
  }, [getData, searchString]);

  useEffect(() => {
    if (selectedNode?.id) {
      getRules(selectedNode.id);
    }
  }, [selectedNode, getRules, selectedTab]);

  useEffect(() => {
    getRulesCount(selectedNode.id);
  }, [selectedNode, getRulesCount]);

  useEffect(() => {
    getData();
  }, [getData, searchString, rulesList]);

  return (
    <Flex style={{ padding: 16 }} direction="vertical" gap={16}>
      <MTabs kind="secondary">
        <MTabs.Link id="rule-captive-portal" suffix={<Badge.Counter size="sm" value={cpRuleCount} />} onClick={() => setSelectedTab("Captive Portal")} selected={selectedTab === "Captive Portal"}>
          {t("proximity-rule.captive-portal-rules-text")}
        </MTabs.Link>
        <MTabs.Link suffix={<Badge.Counter size="sm" value={enRuleCount} />} onClick={() => setSelectedTab("Engagement")} selected={selectedTab === "Engagement"} id="rule-engagement">
          {t("proximity-rule.engagement-rules-text")}
        </MTabs.Link>
        <MTabs.Link id="rule-location-personas" suffix={<Badge.Counter size="sm" value={lpRuleCount} />} onClick={() => setSelectedTab("Profile")} selected={selectedTab === "Profile"}>
          {t("proximity-rule.location-personas-rules-text")}
        </MTabs.Link>
        <MTabs.Link id="rule-density" suffix={<Badge.Counter size="sm" value={densityRuleCount} />} onClick={() => setSelectedTab("Density")} selected={selectedTab === "Density"}>
          {t("proximity-rule.density-rules-text")}
        </MTabs.Link>
      </MTabs>
      <Flex direction="vertical" gap={16}>
        <div style={{ maxWidth: 300, marginTop: 15 }}>
          <Search
            id="search-location"
            placeholder={t("proximity-rule.search-text")}
            onChange={(e) => setSearchString(e?.target?.value)}
            value={searchString}
            onClear={() => setSearchString("")}
            options={[]}
          />
        </div>
        {getFilteredData().length > 0 ? (
          <MTable.Pagination key={selectedTab} currentPage={1} defaultRecordsOption="10" showNavigation="all" showOptions showTotal>
            <MTable isLoading={rulesLoading} columns={getColumns()} data={getFilteredData()} />
          </MTable.Pagination>
        ) : (
          <MTable isLoading={rulesLoading} columns={getColumns()} data={getFilteredData()} />
        )}
      </Flex>
    </Flex>
  );
}

export default ProximityRules;
